import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { messagesApi } from '@/api/messages'
import type { SendMessageRequest } from '@/types/domain'

/**
 * Admin/manager support inbox — wraps CrmService's MessagesController (`messagesApi`)
 * with React Query, local to admin-dashboard (mirrors useOrders.ts's shape: shared-core
 * only ships the raw client for this module, not query wrappers yet). `useInbox` returns
 * EVERY customer conversation for a manager/admin caller (`messagesApi.inbox()` — a
 * customer only ever sees their own single conversation, but that's not this app).
 */

export const messagesQueryKeys = {
  inbox: ['messages', 'inbox'] as const,
  history: (conversationId: string) => ['messages', 'history', conversationId] as const,
}

export function useInbox() {
  return useQuery({
    queryKey: messagesQueryKeys.inbox,
    queryFn: () => messagesApi.inbox(),
    staleTime: 15_000,
  })
}

export function useConversationHistory(conversationId: string | undefined) {
  return useQuery({
    queryKey: messagesQueryKeys.history(conversationId ?? 'none'),
    queryFn: () => messagesApi.history(conversationId as string),
    enabled: !!conversationId,
    staleTime: 5_000,
  })
}

/** A staff reply must always carry `conversationId` — omitting it (customer-only shorthand
 * for "start/continue my own conversation") would 404/misroute for a manager/admin caller. */
export function useSendMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: SendMessageRequest) => messagesApi.send(body),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: messagesQueryKeys.inbox })
      qc.invalidateQueries({ queryKey: messagesQueryKeys.history(result.conversationId) })
    },
  })
}

export function useMarkConversationRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (conversationId: string) => messagesApi.markRead(conversationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messagesQueryKeys.inbox })
    },
  })
}

/** A mutation (not a query) even though it has no side effect on our own data — it's an
 * on-demand AI call to ai-engine, not something to cache/refetch automatically. */
export function useSuggestReply() {
  return useMutation({
    mutationFn: (conversationId: string) => messagesApi.suggestReply(conversationId),
  })
}
